<!--
  TimeWindowSelector — control segmentado 24h / 7d / 30d de Otera.
  Reemplaza a TimeWindowSelectorLegacy. Sincroniza store ↔ URL (Story 2.11).
-->

<script setup>
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useTenantStore } from '@/stores/tenant.js';
import { TIME_WINDOWS } from '@/utils/timeWindows.js';

defineOptions({ name: 'DsTimeWindowSelector' });

const OPTIONS = TIME_WINDOWS;

const tenant = useTenantStore();
const { timeWindow } = storeToRefs(tenant);
const route = useRoute();
const router = useRouter();
const pillRefs = ref([]);

const queryWindow = route.query?.window;
if (
  typeof queryWindow === 'string' &&
  OPTIONS.some((o) => o.value === queryWindow) &&
  queryWindow !== timeWindow.value
) {
  tenant.setTimeWindow(queryWindow);
}

const activeIndex = computed(() => OPTIONS.findIndex((o) => o.value === timeWindow.value));

async function select(value) {
  if (value === timeWindow.value) return;
  tenant.setTimeWindow(value);
  await router.replace({ query: { ...route.query, window: value } });
  await nextTick();
  const idx = OPTIONS.findIndex((o) => o.value === value);
  pillRefs.value[idx]?.focus?.();
}

function onKeyNav(evt, idx) {
  if (evt.key === 'ArrowRight' || evt.key === 'ArrowDown') {
    evt.preventDefault();
    select(OPTIONS[(idx + 1) % OPTIONS.length].value);
  } else if (evt.key === 'ArrowLeft' || evt.key === 'ArrowUp') {
    evt.preventDefault();
    select(OPTIONS[(idx - 1 + OPTIONS.length) % OPTIONS.length].value);
  } else if (evt.key === 'Home') {
    evt.preventDefault();
    select(OPTIONS[0].value);
  } else if (evt.key === 'End') {
    evt.preventDefault();
    select(OPTIONS[OPTIONS.length - 1].value);
  }
}

watch(timeWindow, async (val) => {
  if (route.query.window !== val) {
    await router.replace({ query: { ...route.query, window: val } });
  }
});
</script>

<template>
  <div
    role="radiogroup"
    aria-label="Time window"
    class="tws"
  >
    <button
      v-for="(opt, i) in OPTIONS"
      :key="opt.value"
      ref="pillRefs"
      type="button"
      role="radio"
      :aria-checked="timeWindow === opt.value"
      :tabindex="activeIndex === i ? 0 : -1"
      :class="['tws__pill', { 'tws__pill--active': timeWindow === opt.value }]"
      @click="select(opt.value)"
      @keydown="onKeyNav($event, i)"
    >
      {{ opt.label }}
    </button>
  </div>
</template>

<style scoped>
.tws {
  display: inline-flex;
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--bg-canvas);
  padding: var(--space-1);
  gap: var(--space-1);
}

.tws__pill {
  border: 0;
  background: transparent;
  color: var(--fg-secondary);
  font-size: var(--text-sm);
  font-weight: var(--fw-medium);
  padding: 0 var(--space-3);
  height: var(--control-sm);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background-color var(--motion-fast) var(--motion-ease-out),
              color var(--motion-fast) var(--motion-ease-out);
}

.tws__pill:hover {
  color: var(--fg-primary);
}

.tws__pill--active {
  background: var(--bg-muted);
  color: var(--fg-primary);
}
</style>
